import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';

import { Table, Th, Td, Tr } from 'common/components/organisms/DataTable';
import { GhostBtn } from 'common/components/molecules/ListToolbar';
import { Download, UploadIcon, AlertTriangle } from 'common/images/FaIcons';
import ImportStepIndicator from './ImportStepIndicator';
import {
  columnLabel, problemKey, problemMessage, problemsToCsv
} from './helpers';
import { MAX_ROWS_SHOWN } from '../../machineCsv.constants';
import {
  Body, Muted, Callout, Footer, FooterActions, TableScroll, Pill, PrimaryAction
} from './MachineCsv.styled';

function ImportProblemsStep({
  problems, fileName, afterApply, onUploadAgain
}) {
  const { t } = useTranslation();

  const downloadProblems = useCallback(() => {
    const blob = new Blob([problemsToCsv(t, problems, afterApply)], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${(fileName || 'assets').replace(/\.csv$/i, '')}-problems.csv`);
  }, [t, problems, fileName, afterApply]);

  const headline = afterApply
    ? t('assets.csv.problems.headlineAfterApply', {
      count: problems.length,
      defaultValue: 'Nothing was changed. {{count}} changes no longer fit the site, usually because someone edited these machines after your preview.'
    })
    : t('assets.csv.problems.headline', {
      count: problems.length,
      fileName,
      defaultValue: '{{count}} problems in {{fileName}}. Nothing has been changed.'
    });
  const advice = afterApply
    ? t('assets.csv.problems.adviceAfterApply', { defaultValue: 'Export again to get the current values, redo your edits, and import that file.' })
    : t('assets.csv.problems.advice', { defaultValue: 'Fix these rows in your file and upload it again.' });

  return (
    <Body>
      <Callout tone="crit" role="alert">
        <AlertTriangle />
        <div>
          <b>{headline}</b>
          {' '}
          {advice}
        </div>
      </Callout>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              {!afterApply && <Th>{t('assets.csv.problemsTable.row', { defaultValue: 'Row' })}</Th>}
              <Th>{columnLabel(t, 'id')}</Th>
              <Th>{t('assets.csv.problemsTable.column', { defaultValue: 'Column' })}</Th>
              <Th>{t('assets.csv.problemsTable.problem', { defaultValue: 'Problem' })}</Th>
            </tr>
          </thead>
          <tbody>
            {problems.slice(0, MAX_ROWS_SHOWN).map((problem, index) => (
              <Tr key={problemKey(problem, index)}>
                {!afterApply && <Td>{problem.row}</Td>}
                <Td>{problem.machine_id}</Td>
                <Td><Pill>{columnLabel(t, problem.column)}</Pill></Td>
                <Td>{problemMessage(t, problem)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
      {problems.length > MAX_ROWS_SHOWN && (
        <Muted>
          {t('assets.csv.problems.notShown', {
            count: problems.length - MAX_ROWS_SHOWN,
            defaultValue: '{{count}} more problems are not listed here. Download the problems CSV to see them all.'
          })}
        </Muted>
      )}
      {!afterApply && (
        <Muted>{t('assets.csv.problems.rowHint', { defaultValue: 'Row numbers match your spreadsheet (row 1 is the header).' })}</Muted>
      )}
      <Footer>
        <ImportStepIndicator current="problems" />
        <FooterActions>
          <GhostBtn type="button" onClick={downloadProblems}>
            <Download />
            {t('assets.csv.problems.download', { defaultValue: 'Problems CSV' })}
          </GhostBtn>
          <PrimaryAction type="button" onClick={onUploadAgain}>
            <UploadIcon />
            {t('assets.csv.problems.uploadAgain', { defaultValue: 'Upload a fixed file' })}
          </PrimaryAction>
        </FooterActions>
      </Footer>
    </Body>
  );
}

ImportProblemsStep.propTypes = {
  problems: PropTypes.arrayOf(PropTypes.shape({
    row: PropTypes.number,
    machine_id: PropTypes.number,
    column: PropTypes.string,
    code: PropTypes.string.isRequired,
    params: PropTypes.object
  })).isRequired,
  fileName: PropTypes.string,
  afterApply: PropTypes.bool,
  onUploadAgain: PropTypes.func.isRequired
};

ImportProblemsStep.defaultProps = {
  fileName: null,
  afterApply: false
};

export default ImportProblemsStep;
